import React from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

/**
 * Renders the list of tool calls made during the chat session in the sidebar.
 * Allows expanding each call to view request and response details.
 * 
 * @param {object} props - Component props.
 * @param {Array} props.apiCalls - Array of tool call objects (including request and response).
 * @param {object} props.expandedApiCalls - Object mapping call index to boolean (expanded state).
 * @param {function} props.toggleApiCall - Function to toggle the expanded state of a call by index.
 */
export default function ToolCallList({ apiCalls, expandedApiCalls, toggleApiCall }) {
    if (!apiCalls || apiCalls.length === 0) {
        return <p className="text-sm text-gray-600">No tool calls made yet.</p>;
    }

    return (
        <div className="space-y-3">
            {apiCalls.map((call, index) => {
                // Extract data, supporting different potential structures (e.g., function call vs direct call)
                const toolName = call.name || call.function?.name || 'Unknown Tool';
                const toolArgs = call.arguments || call.function?.arguments || '{}';
                const toolId = call.id || `tool-${index}`; // Use call ID if available
                const toolResponse = call.response; // Response should be attached by useChatManager
                const timestamp = call.timestamp || new Date().toLocaleTimeString(); // Timestamp added by useChatManager

                // Attempt to parse arguments for better display
                let parsedArgs;
                let argsString = '';
                try {
                    parsedArgs = typeof toolArgs === 'string' ? JSON.parse(toolArgs) : toolArgs;
                    // Create a concise string representation for the collapsed view
                    if (typeof parsedArgs === 'object' && parsedArgs !== null) {
                         argsString = Object.entries(parsedArgs)
                            .map(([k, v]) => `${k}: ${typeof v === 'string' ? `"${v}"` : JSON.stringify(v)}`)
                            .join(', ');
                    } else {
                        argsString = String(parsedArgs);
                    }
                } catch (e) {
                    parsedArgs = toolArgs; // Keep original if parsing fails
                    argsString = String(toolArgs);
                }
                 // Truncate long argument strings in collapsed view
                 const maxCollapsedArgLength = 50;
                 const truncatedArgsString = argsString.length > maxCollapsedArgLength 
                    ? argsString.substring(0, maxCollapsedArgLength) + '...' 
                    : argsString;


                const isExpanded = expandedApiCalls[index] || false;

                return (
                    <div key={toolId} className="bg-white rounded-md border p-3 shadow-sm">
                        <div
                            className="flex items-center justify-between cursor-pointer mb-1"
                            onClick={() => toggleApiCall(index)} // Use the passed function
                            aria-expanded={isExpanded}
                            aria-controls={`tool-details-${toolId}`}
                        >
                            <span className="font-medium text-gray-700 text-sm truncate" title={toolName}>
                                {toolName} #{index + 1}
                            </span>
                            {isExpanded ? (
                                <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            ) : (
                                <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            )}
                        </div>

                        <div className="text-xs text-gray-500">
                            {timestamp}
                        </div>

                        {/* Collapsed view: Show truncated arguments */}
                        {!isExpanded && (
                            <div className="text-xs text-gray-500 truncate mt-1" title={argsString}>
                                Args: {truncatedArgsString || 'N/A'}
                            </div>
                        )}

                        {/* Expanded view: Show full details */}
                        {isExpanded && (
                            <div id={`tool-details-${toolId}`} className="mt-2 space-y-2 text-xs">
                                {/* Request Arguments */}
                                <div className="p-2 bg-gray-50 rounded border">
                                    <div className="font-semibold text-gray-600">Request Args:</div>
                                    <pre className="p-1 bg-gray-800 text-white rounded text-xs overflow-x-auto mt-1 whitespace-pre-wrap break-all">
                                        {JSON.stringify(parsedArgs, null, 2)}
                                    </pre>
                                </div>

                                {/* Response */}
                                <div className="p-2 bg-gray-50 rounded border">
                                    <div className="font-semibold text-gray-600">Response:</div>
                                    {toolResponse !== undefined && toolResponse !== null ? (
                                        <pre className="p-1 bg-gray-800 text-white rounded text-xs overflow-x-auto mt-1 whitespace-pre-wrap break-all">
                                            {typeof toolResponse === 'string'
                                                ? toolResponse
                                                : JSON.stringify(toolResponse, null, 2)}
                                        </pre>
                                    ) : (
                                        <div className="italic text-gray-500 mt-1">No response data recorded</div>
                                    )}
                                </div>

                                {/* Tool Call ID */}
                                <div className="text-xs text-gray-400 pt-1">
                                    ID: {toolId}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}